import App from 'next/app'
import { Head, HreflangLinks, JsonLd, LocaleSync, NextNProgress, Page } from '@/components'
import api, { createApi } from '@/lib/api'
import '@/assets/styles/fonts.css'
import { HostProvider } from '@/contexts/HostContext'
import { ScheduleProvider } from '@/contexts/ScheduleContext'
import { buildMedicalClinic } from '@/lib/schema'
import getConfig from 'next/config'
import * as Sentry from '@sentry/node'
import Error from './_error.js'
import { buildGtmDataLayer, pushGtmDataLayer } from '@/lib/gtm'
import { getOgImageUrl } from '@/lib/ogImage'
import CookiesBanner from '@/components/CookiesBanner/CookieBanner'
import { initAttribution } from '@/lib/attribution'
import '@/lib/i18n';

const isProdMode = process.env.NODE_ENV === 'production'

const {
  publicRuntimeConfig: { host, sentryDsn },
} = getConfig()

Sentry.init({
  dsn: `${sentryDsn}`,
  enabled: process.env.NODE_ENV === 'production',
})

export default class MyApp extends App {
  // store errorStatus here if page with error was rendered on server
  static lastStatusCode = null

  static async getInitialProps({ Component, ctx }) {
    let pageProps = {}

    const currentApi = ctx.req ? createApi() : api

    let errorStatus = ctx.err ? ctx.err.statusCode : ctx.res ? ctx.res.statusCode : null
    let hasError = false;

    if (ctx.err) {
      Sentry.captureException(ctx.err)
    }

    const menuPromise = currentApi.menu()
    const schedulePromise = currentApi.schedule()

    try {
      if (Component.getInitialProps) {
        pageProps = await Component.getInitialProps({
          ...ctx,
          api: currentApi,
          statusCode: errorStatus || MyApp.lastStatusCode,
        })
      }
    } catch (error) {
      errorStatus = error.statusCode || 500
      hasError = true
      ctx.res && ctx.res.status(errorStatus)
      console.error(error)
      Sentry.captureException(error)
    }

    if (ctx.res?.headersSent || ctx.res?.finished) {
      return { pageProps }
    }

    const [menu, schedule] = await Promise.all([menuPromise, schedulePromise])

    const currentHost =
      process.env.NODE_ENV === 'development'
        ? host
        : ctx.req
          ? `${ctx.req.hostname}`
          : location.host

    const asPath = ctx.asPath || ctx.pathname || '/'
    const gtmDataLayer = buildGtmDataLayer(pageProps, asPath)

    return {
      pageProps,
      gtmDataLayer,
      hasError: Component === Error || hasError,
      host: currentHost,
      errorStatus,
      menu,
      schedule,
      apiCacheData: currentApi.extractCache(),
    }
  }

  constructor(props) {
    super(props)
    if (typeof window !== 'undefined') {
      api.fillCache(this.props.apiCacheData)
    }
  }

  componentDidMount() {
    // initAttribution(api.attributionCreate)
    if (this.props.errorStatus && +this.props.errorStatus !== 200) {
      // save errorStatus if page with error was rendered on server

      MyApp.lastStatusCode = this.props.errorStatus;
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.router.asPath === this.props.router.asPath) return
    // initAttribution(api.attributionCreate)
    if (!isProdMode) return
    pushGtmDataLayer(this.props.gtmDataLayer, { event: 'page_context' })
  }

  render() {
    const { Component, pageProps, menu, schedule, hasError, host } = this.props;
    const decodedStr = decodeURIComponent(this.props.router.asPath)
    const hasCyrillic = /[А-Яа-яЁё]/.test(decodedStr);

    const errorStatus = hasCyrillic ? 404 : this.props.errorStatus || MyApp.lastStatusCode
    const skipSeoLinks = hasError && errorStatus === 404
    const scheduleData = schedule?.data ?? [];
    const clinicSchema = buildMedicalClinic({
      directions: menu?.directions || menu?.data?.directions || [],
    })
    const ogImage = getOgImageUrl(pageProps, host)

  
    if (hasError) {
      return errorStatus === 404 ? (
        <>
          <Head />
          <HreflangLinks path={pageProps?.canonicalPath} ogImage={ogImage} skipSeoLinks={skipSeoLinks}/>
          <JsonLd data={clinicSchema} />
          <LocaleSync />
          <ScheduleProvider data={scheduleData}>
            <Page menu={menu}>
              <NextNProgress />
              <Error {...pageProps} statusCode={errorStatus} />
            </Page>
          </ScheduleProvider>
        </>
      ) : (
        <>
          <Head />
          <HreflangLinks path={pageProps?.canonicalPath} ogImage={ogImage} skipSeoLinks={skipSeoLinks}/>
          <JsonLd data={clinicSchema} />
          <LocaleSync />
          <NextNProgress />
          <Error {...pageProps} statusCode={errorStatus} />
        </>
      )
    }

    return (
      <>
        <Head />
        <HreflangLinks path={pageProps?.canonicalPath} ogImage={ogImage} skipSeoLinks={skipSeoLinks}/>
        <JsonLd data={clinicSchema} />
        <LocaleSync />
        <HostProvider host={host}>
          <ScheduleProvider data={scheduleData}>
            <Page menu={menu} {...pageProps}>
              <NextNProgress />
              <Component {...pageProps} />
              <CookiesBanner />
            </Page>
          </ScheduleProvider>
        </HostProvider>
      </>
    )
  }
}
